import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, TestRunWithProgress } from '../services/api';
import { TestExecutionWithDetails } from '../types';
import TestExecutionModal from '../components/TestExecutionModal';
import { emailToUsername, formatDateTime } from '../utils/formatters';
import './TestRunDetailPage.css';

const TestRunDetailPage: React.FC = () => {
  const { testRunId } = useParams<{ testRunId: string }>();
  const navigate = useNavigate();
  const [testRun, setTestRun] = useState<TestRunWithProgress | null>(null);
  const [executions, setExecutions] = useState<TestExecutionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedExecution, setSelectedExecution] = useState<TestExecutionWithDetails | null>(null);
  const [showExecutionModal, setShowExecutionModal] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (testRunId) {
      loadTestRun();
      loadExecutions();
    }
  }, [testRunId, statusFilter]);

  const loadTestRun = async () => {
    try {
      const run = await api.getTestRun(testRunId!);
      setTestRun(run);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load test run');
    }
  };

  const loadExecutions = async () => {
    try {
      setLoading(true);
      const execs = await api.getTestRunExecutions(testRunId!, statusFilter || undefined);
      setExecutions(execs);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRun = async () => {
    try {
      await api.startTestRun(testRunId!);
      await loadTestRun();
    } catch (err: any) {
      setError(err.message || 'Failed to start test run');
    }
  };

  const handleCloseRun = async () => {
    if (!window.confirm('Are you sure you want to close this test run? It will become immutable.')) {
      return;
    }
    try {
      await api.closeTestRun(testRunId!);
      await loadTestRun();
    } catch (err: any) {
      setError(err.message || 'Failed to close test run');
    }
  };

  const handleExecuteTestCase = (execution: TestExecutionWithDetails) => {
    setSelectedExecution(execution);
    setShowExecutionModal(true);
  };

  const handleExecutionComplete = () => {
    setShowExecutionModal(false);
    setSelectedExecution(null);
    loadTestRun();
    loadExecutions();
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'NOT_RUN': return 'status-not-run';
      case 'PASS': return 'status-pass';
      case 'FAIL': return 'status-fail';
      case 'BLOCKED': return 'status-blocked';
      default: return '';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace('_', ' ');
  };

  const formatDate = (dateString?: string) => {
    return dateString ? formatDateTime(dateString) : 'N/A';
  };

  if (loading && !testRun) {
    return <div className="test-run-detail-page"><div className="loading">Loading...</div></div>;
  }

  if (!testRun) {
    return <div className="test-run-detail-page"><div className="error-message">Test run not found</div></div>;
  }

  const isImmutable = testRun.status === 'COMPLETED' || testRun.status === 'ABORTED';

  return (
    <div className="test-run-detail-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/test-runs')}>
          ← Back to Test Runs
        </button>
        <div className="header-actions">
          {testRun.status === 'NOT_STARTED' && (
            <button className="btn-primary" onClick={handleStartRun}>
              Start Execution
            </button>
          )}
          {testRun.status === 'IN_PROGRESS' && (
            <button className="btn-success" onClick={handleCloseRun}>
              Close Test Run
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="test-run-header">
        <div className="header-left">
          <h1>{testRun.name}</h1>
          {testRun.description && <p className="description">{testRun.description}</p>}
        </div>
        <span className={`status-badge ${getStatusBadgeClass(testRun.status)}`}>
          {getStatusLabel(testRun.status)}
        </span>
      </div>

      <div className="test-run-info">
        <div className="info-grid">
          <div className="info-item">
            <span className="label">Build Version:</span>
            <span className="value">{testRun.build_version || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="label">Environment:</span>
            <span className="value">{testRun.environment || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="label">Created By:</span>
            <span className="value">{emailToUsername(testRun.created_by)} on {formatDate(testRun.created_at)}</span>
          </div>
          {testRun.started_at && (
            <div className="info-item">
              <span className="label">Started By:</span>
              <span className="value">{testRun.started_by ? emailToUsername(testRun.started_by) : 'N/A'} on {formatDate(testRun.started_at)}</span>
            </div>
          )}
          {testRun.completed_at && (
            <div className="info-item">
              <span className="label">Completed By:</span>
              <span className="value">{testRun.completed_by ? emailToUsername(testRun.completed_by) : 'N/A'} on {formatDate(testRun.completed_at)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="progress-section">
        <h2>Execution Progress</h2>
        <div className="progress-bar-large">
          <div className="progress-fill" style={{ width: `${testRun.progress_percentage}%` }} />
        </div>
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-value">{testRun.total_cases}</div>
            <div className="metric-label">Total Cases</div>
          </div>
          <div className="metric-card pass">
            <div className="metric-value">{testRun.pass_count}</div>
            <div className="metric-label">Passed</div>
          </div>
          <div className="metric-card fail">
            <div className="metric-value">{testRun.fail_count}</div>
            <div className="metric-label">Failed</div>
          </div>
          <div className="metric-card blocked">
            <div className="metric-value">{testRun.blocked_count}</div>
            <div className="metric-label">Blocked</div>
          </div>
          <div className="metric-card not-run">
            <div className="metric-value">{testRun.not_run_count}</div>
            <div className="metric-label">Not Run</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{testRun.pass_percentage.toFixed(1)}%</div>
            <div className="metric-label">Pass Rate</div>
          </div>
        </div>
      </div>

      <div className="executions-section">
        <div className="section-header">
          <h2>Test Cases</h2>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="NOT_RUN">Not Run</option>
            <option value="PASS">Pass</option>
            <option value="FAIL">Fail</option>
            <option value="BLOCKED">Blocked</option>
          </select>
        </div>

        {loading ? (
          <div className="loading">Loading test cases...</div>
        ) : (
          <div className="executions-table">
            <table>
              <thead>
                <tr>
                  <th>Test Case</th>
                  <th>Status</th>
                  <th>Executed By</th>
                  <th>Executed At</th>
                  <th>Defect</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((execution) => (
                  <tr key={execution.id}>
                    <td>
                      <div className="test-case-info">
                        {execution.test_case_display_id && (
                          <span className="test-case-id-badge">{execution.test_case_display_id}</span>
                        )}
                        <div className="test-case-description">{execution.test_case_description}</div>
                        {execution.test_case_expected_result && (
                          <div className="test-case-preconditions">
                            Expected: {execution.test_case_expected_result}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(execution.execution_status)}`}>
                        {getStatusLabel(execution.execution_status)}
                      </span>
                    </td>
                    <td>{execution.executed_by || '-'}</td>
                    <td>{formatDate(execution.executed_at)}</td>
                    <td>{execution.defect_reference || '-'}</td>
                    <td>
                      {!isImmutable && (
                        <button
                          className="btn-execute"
                          onClick={() => handleExecuteTestCase(execution)}
                        >
                          {execution.execution_status === 'NOT_RUN' ? 'Execute' : 'Re-execute'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showExecutionModal && selectedExecution && (
        <TestExecutionModal
          execution={selectedExecution}
          onClose={() => setShowExecutionModal(false)}
          onComplete={handleExecutionComplete}
        />
      )}
    </div>
  );
};

export default TestRunDetailPage;
